import { Component, ContentType } from 'contensis-core-api';
import RepositoryBaseService from './RepositoryBaseService';
import { ModelDependencies, ResourcesToMigrate } from '~/models';
import ContensisRepository from '~/repositories/ContensisRepository';
import { ContentTypeBuilder } from '~/transformations/ContentTypeBuilder';

class ModelsService extends RepositoryBaseService {
  migrate: {
    contentTypes: ContentTypeBuilder[];
    components: ContentTypeBuilder<Component>[];
  } = { contentTypes: [], components: [] };
  resources!: ResourcesToMigrate;

  get toMigrate() {
    return [...this.migrate.contentTypes, ...this.migrate.components];
  }

  constructor(
    // contensis is the current repository this instance is attached to
    contensis: ContensisRepository,
    // source is always the source repository
    source: ContensisRepository
  ) {
    super(contensis, source);
  }

  BuildResourcesToMigrate = (resources: ResourcesToMigrate) => {
    const { contentTypeIds, componentIds } = (this.resources = resources);
    const { source } = this;

    // Iterate all resources and build a version of it specifically for the target
    for (const id of contentTypeIds) {
      const contentType = source.context.resolve.contentType(id);

      if (contentType) {
        const built = this.BuildResource(contentType);
        this.migrate.contentTypes.push(built);
      }
    }
    for (const id of componentIds) {
      const component = source.context.resolve.component(id);

      if (component) {
        const built = this.BuildResource(component);
        this.migrate.components.push(built);
      }
    }
  };

  /**
   * Use ContentTypeBuilder to create an instance of a resource specifically
   * for the target repository, locating the dependencies narrowed to those
   * included in the current migration
   */
  BuildResource = <T extends ContentType | Component>(resource: T) => {
    const { contensis, source, resources } = this;
    const md = source.context.dependencies.getById(
      resource.id
    ) as ModelDependencies;
    this.NarrowDependencies(md, resources);
    const built = new ContentTypeBuilder(contensis, source, resource, md);
    return built;
  };

  /**
   * Narrow the retrieved dependencies for the current resource to include only those
   * involved in the current migration process
   */
  NarrowDependencies = (
    md: ModelDependencies | undefined,
    narrowBy: ResourcesToMigrate
  ) => {
    if (!md) return { dependencies: [], dependencyOf: [] };
    const { contentTypeIds, componentIds } = narrowBy;

    if (md.dependencies?.contentTypes)
      md.dependencies.contentTypes = md.dependencies.contentTypes.filter(
        (cid) => contentTypeIds.includes(cid)
      );

    if (md.dependencies?.components)
      md.dependencies.components = md.dependencies.components.filter((cid) =>
        componentIds.includes(cid)
      );

    if (md.dependencyOf?.contentTypes)
      md.dependencyOf.contentTypes = md.dependencyOf.contentTypes.filter(
        (cid) => contentTypeIds.includes(cid)
      );

    if (md.dependencyOf?.components)
      md.dependencyOf.components = md.dependencyOf.components.filter((cid) =>
        componentIds.includes(cid)
      );
  };
}

export default ModelsService;
